import React from 'react';
import {
  View,
  Text,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import {
  Send,
  CheckCircle,
  Mail,
  Info,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { AppModal, ModalAction } from '@/components/modal';

interface InviteFounderModalProps {
  visible: boolean;
  onClose: () => void;
  startupName: string;
  founderName?: string;
  founderEmail?: string;
  inviteEmail: string;
  onSetInviteEmail: (v: string) => void;
  inviteSending: boolean;
  onSubmit: () => void;
}

export function InviteFounderModal({
  visible,
  onClose,
  startupName,
  founderName,
  founderEmail,
  inviteEmail,
  onSetInviteEmail,
  inviteSending,
  onSubmit,
}: InviteFounderModalProps) {
  const colors = useThemeColors();

  const footer = (
    <AppModal.Actions>
      <ModalAction
        label={inviteSending ? 'Sending…' : 'Send Invitation'}
        variant="primary"
        disabled={inviteSending || !inviteEmail.trim()}
        icon={inviteSending
          ? <ActivityIndicator size="small" color="white" />
          : <Mail size={20} color="white" />}
        onPress={onSubmit}
        accessibilityLabel="Send invitation to founder"
      />
    </AppModal.Actions>
  );

  return (
    <AppModal
      visible={visible}
      onClose={onClose}
      title="Invite Founder"
      maxWidth={480}
      footer={footer}
    >
          {/* Header Icon */}
          <View className="items-center mb-6">
            <View
              className="w-16 h-16 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: colors.primary[50] }}
            >
              <Send size={32} color={colors.primary[600]} />
            </View>
            <Text style={{ color: colors.text.primary }} className="text-xl font-bold text-center">
              Connect with {startupName}
            </Text>
            <Text style={{ color: colors.text.secondary }} className="text-sm text-center mt-2">
              Invite the founder to claim their startup profile and unlock full due diligence capabilities
            </Text>
          </View>

          {/* Benefits */}
          <View
            className="rounded-2xl p-4 mb-6"
            style={{ backgroundColor: colors.surface.primary }}
          >
            <Text style={{ color: colors.text.primary }} className="font-semibold mb-3">
              What happens when founder claims:
            </Text>
            <View className="gap-3">
              {[
                'Connect verified data room with financials',
                'Enable real-time startup updates',
                'Access detailed due diligence materials',
                'Direct communication channel',
              ].map((benefit, index) => (
                <View key={index} className="flex-row items-center gap-3">
                  <View
                    className="w-6 h-6 rounded-full items-center justify-center"
                    style={{ backgroundColor: colors.traffic.greenLight }}
                  >
                    <CheckCircle size={14} color={colors.success} />
                  </View>
                  <Text style={{ color: colors.text.secondary }} className="text-sm flex-1">
                    {benefit}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          {/* Email Input */}
          <View className="mb-6">
            <Text style={{ color: colors.text.primary }} className="font-medium mb-2">
              {founderName ? `${founderName}'s Email` : "Founder's Email Address"}
            </Text>
            <TextInput
              value={inviteEmail}
              onChangeText={onSetInviteEmail}
              placeholder="founder@startup.com"
              placeholderTextColor={colors.text.tertiary}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              accessibilityLabel="Founder email address"
              className="rounded-xl px-4 py-4"
              style={{
                backgroundColor: colors.surface.primary,
                color: colors.text.primary,
                borderWidth: 1,
                borderColor: founderEmail ? colors.success : colors.border.light,
                fontSize: 16,
              }}
            />
            {founderEmail && inviteEmail === founderEmail ? (
              <View className="flex-row items-center gap-1.5 mt-2">
                <CheckCircle size={12} color={colors.success} />
                <Text style={{ color: colors.success }} className="text-xs">
                  Email extracted from pitch deck
                </Text>
              </View>
            ) : (
              <Text style={{ color: colors.text.tertiary }} className="text-xs mt-2">
                We'll send an invitation with your profile as the requesting investor
              </Text>
            )}
          </View>

          {/* Privacy Note */}
          <View
            className="rounded-xl p-3 mt-4 flex-row items-start gap-2"
            style={{ backgroundColor: colors.amber.light }}
          >
            <Info size={16} color={colors.warning} style={{ marginTop: 2 }} />
            <Text style={{ color: colors.text.secondary }} className="text-xs flex-1">
              The founder will see your investor profile and that you analyzed their pitch deck.
              They can choose whether to claim the profile and connect with you.
            </Text>
          </View>
    </AppModal>
  );
}
